/**
 * Persistentes STOCK_MOVEMENT-Ledger (append-only).
 * ADR-0013 / ADR-0016 Phase 1:
 *  - parallel movement write (Lots bleiben vorerst primär)
 *  - ledgerSequence pro storeId
 *  - correlationId pflicht (defaulted)
 *  - qtyBefore / qtyAfter
 *  - keine Truncation (Archiv später; Wissen nie löschen)
 */

// Note: do not import businessPayloadWrite at top-level — it creates a cycle via
// sales/cache/inventory aggregates that import this module.

import { resolveMovementActorMeta } from './movementActorContext';
import { applyStockSnapshotsAfterMovements } from '../snapshots/stockSnapshot';
import { applyMovementsToLotProjection } from '../projection/lotProjectionFromMovements';
import {
  QUANTITY_UOM_BASE,
  stampMovementBaseUnits,
  packagingAuditMetaFromCatalog
} from '../packaging/baseUnits';
import { ensureBusinessStores, DEFAULT_STORE_ID } from '../stores/storeCatalog';

export { DEFAULT_STORE_ID };

export const STOCK_MOVEMENT_TYPES = {
  PURCHASE: 'PURCHASE',
  SALE: 'SALE',
  SALE_RETURN: 'SALE_RETURN',
  VOID: 'VOID',
  TRANSFER_OUT: 'TRANSFER_OUT',
  TRANSFER_IN: 'TRANSFER_IN',
  EXPIRY: 'EXPIRY',
  ADJUSTMENT: 'ADJUSTMENT',
  CONSUMPTION: 'CONSUMPTION',
  /** Restaurant production: raw/WIP components consumed into a batch */
  PRODUCTION_CONSUME: 'PRODUCTION_CONSUME',
  /** Restaurant production: finished / semi-finished goods received */
  PRODUCTION_OUTPUT: 'PRODUCTION_OUTPUT'
};

export const MOVEMENT_SCHEMA_VERSION = 2;

export const COST_METHOD_FIFO = 'FIFO';
export const COST_METHOD_VERSION = 1;

/** Audit packaging stamp when qty is already base (sale/expiry/void/…); null if unitsPerPack≤1. */
function resolvePackagingAuditMeta(...sources) {
  for (const src of sources) {
    if (!src || typeof src !== 'object') continue;
    if (src.packaging && typeof src.packaging === 'object') return src.packaging;
    const fromCatalog = packagingAuditMetaFromCatalog(src);
    if (fromCatalog) return fromCatalog;
  }
  return null;
}

/**
 * Soft memory soft-limit for *active* partition only.
 * Overflow is moved to stockMovementsArchive (never deleted).
 * null / 0 = keep everything in active (default until archival UX exists).
 */
const ACTIVE_PARTITION_SOFT_MAX = 0;

export function ensureStockMovementsArray(parsed) {
  if (!parsed || typeof parsed !== 'object') {
    return [];
  }
  if (!Array.isArray(parsed.stockMovements)) {
    parsed.stockMovements =
      parsed.stockMovements && typeof parsed.stockMovements === 'object'
        ? Object.values(parsed.stockMovements)
        : [];
  }
  return parsed.stockMovements;
}

export function ensureStockMovementsArchiveArray(parsed) {
  if (!parsed || typeof parsed !== 'object') {
    return [];
  }
  if (!Array.isArray(parsed.stockMovementsArchive)) {
    parsed.stockMovementsArchive = Array.isArray(parsed.stockMovementsArchive)
      ? parsed.stockMovementsArchive
      : [];
  }
  return parsed.stockMovementsArchive;
}

/**
 * @param {object} parsed
 * @returns {{ sequencesByStore: Record<string, number> }}
 */
export function ensureInventoryLedgerMeta(parsed) {
  if (!parsed || typeof parsed !== 'object') {
    return { sequencesByStore: {} };
  }
  if (!parsed.inventoryLedgerMeta || typeof parsed.inventoryLedgerMeta !== 'object') {
    parsed.inventoryLedgerMeta = { sequencesByStore: {} };
  }
  if (
    !parsed.inventoryLedgerMeta.sequencesByStore ||
    typeof parsed.inventoryLedgerMeta.sequencesByStore !== 'object'
  ) {
    parsed.inventoryLedgerMeta.sequencesByStore = {};
  }
  return parsed.inventoryLedgerMeta;
}

/**
 * @param {object} [parsed]
 * @param {object} [entry]
 * @returns {string}
 */
export function resolveStoreId(parsed, entry = {}) {
  if (entry.storeId != null && String(entry.storeId).trim()) {
    return String(entry.storeId).trim();
  }
  if (parsed?.defaultStoreId != null && String(parsed.defaultStoreId).trim()) {
    return String(parsed.defaultStoreId).trim();
  }
  const stores = Array.isArray(parsed?.stores) ? parsed.stores : [];
  const first = stores.find((s) => s && (s.id != null || s.storeId != null));
  if (first) {
    return String(first.id ?? first.storeId);
  }
  return DEFAULT_STORE_ID;
}

function generateMovementId() {
  return `sm_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @param {string|number|null|undefined} [seed]
 */
export function generateCorrelationId(seed = null) {
  if (seed != null && String(seed).trim()) {
    return `corr_${String(seed).trim()}`;
  }
  return `corr_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Next ledgerSequence for store (side-effect on parsed.inventoryLedgerMeta).
 * @param {object} parsed
 * @param {string} storeId
 * @returns {number}
 */
export function allocateLedgerSequence(parsed, storeId) {
  const meta = ensureInventoryLedgerMeta(parsed);
  const key = storeId || DEFAULT_STORE_ID;
  const prev = Number(meta.sequencesByStore[key]) || 0;
  // Bootstrap from max existing sequence if meta was missing
  if (prev === 0) {
    const list = ensureStockMovementsArray(parsed);
    const archive = ensureStockMovementsArchiveArray(parsed);
    let max = 0;
    for (const m of list) {
      if (String(m.storeId || DEFAULT_STORE_ID) !== key) continue;
      const s = Number(m.ledgerSequence) || 0;
      if (s > max) max = s;
    }
    for (const m of archive) {
      if (String(m.storeId || DEFAULT_STORE_ID) !== key) continue;
      const s = Number(m.ledgerSequence) || 0;
      if (s > max) max = s;
    }
    meta.sequencesByStore[key] = max;
  }
  const next = (Number(meta.sequencesByStore[key]) || 0) + 1;
  meta.sequencesByStore[key] = next;
  return next;
}

function sumProductQtyFromLots(parsed, productId, location) {
  const lots = Array.isArray(parsed?.lots)
    ? parsed.lots
    : parsed?.lots && typeof parsed.lots === 'object'
      ? Object.values(parsed.lots)
      : [];
  const locKey = location ? String(location).trim().toLowerCase() : '';
  let sum = 0;
  for (const lot of lots) {
    if (!lot || String(lot.productId) !== String(productId)) continue;
    const lotLoc = String(lot.location || 'Principal').trim().toLowerCase();
    if (locKey && lotLoc !== locKey) continue;
    sum += Number(lot.remainingQuantity) || 0;
  }
  return sum;
}

/**
 * Lots are usually already mutated when movements are appended.
 * Recover pre-batch qty, then assign qtyBefore/qtyAfter forward through the batch.
 * @param {object} parsed
 * @param {object[]} normalizedEntries — with quantity signed, productId, location, storeId
 */
export function assignQtyBeforeAfterForBatch(parsed, normalizedEntries) {
  if (!Array.isArray(normalizedEntries) || normalizedEntries.length === 0) return;

  /** @type {Map<string, number>} */
  const deltas = new Map();
  for (const m of normalizedEntries) {
    const key = `${m.storeId}|${m.productId}|${m.location || 'Principal'}`;
    deltas.set(key, (deltas.get(key) || 0) + (Number(m.quantity) || 0));
  }

  /** @type {Map<string, number>} */
  const running = new Map();
  for (const key of deltas.keys()) {
    const [, productId, location] = key.split('|');
    const finalQty = sumProductQtyFromLots(parsed, productId, location);
    const batchDelta = deltas.get(key) || 0;
    running.set(key, finalQty - batchDelta);
  }

  for (const m of normalizedEntries) {
    const key = `${m.storeId}|${m.productId}|${m.location || 'Principal'}`;
    const hasBefore = m.qtyBefore != null && Number.isFinite(Number(m.qtyBefore));
    const hasAfter = m.qtyAfter != null && Number.isFinite(Number(m.qtyAfter));
    if (hasBefore && hasAfter) {
      running.set(key, Number(m.qtyAfter));
      continue;
    }
    const before = hasBefore
      ? Number(m.qtyBefore)
      : running.has(key)
        ? running.get(key)
        : sumProductQtyFromLots(parsed, m.productId, m.location);
    const qty = Number(m.quantity) || 0;
    const after = hasAfter ? Number(m.qtyAfter) : before + qty;
    m.qtyBefore = before;
    m.qtyAfter = after;
    running.set(key, after);
  }
}

/**
 * @param {object} entry
 * @param {{ storeId?: string, correlationId?: string, skipSequence?: boolean }} [defaults]
 */
export function normalizeStockMovement(entry, defaults = {}) {
  // ADR-0016: ledger quantities are always base units (pack multipliers applied here)
  const baseEntry = stampMovementBaseUnits(entry || {});
  const qty = Number(baseEntry.quantity ?? baseEntry.quantitySigned);
  const now = baseEntry.occurredAt || baseEntry.createdAt || new Date().toISOString();
  const recordedAt = baseEntry.recordedAt || new Date().toISOString();
  const location = baseEntry.location || baseEntry.locationId || 'Principal';
  const storeId =
    (baseEntry.storeId != null && String(baseEntry.storeId).trim()) ||
    defaults.storeId ||
    DEFAULT_STORE_ID;
  const correlationId =
    (baseEntry.correlationId != null && String(baseEntry.correlationId).trim()) ||
    defaults.correlationId ||
    generateCorrelationId(
      baseEntry.referenceId != null ? baseEntry.referenceId : baseEntry.movementId || null
    );

  const unitCostRaw = baseEntry.unitCost;
  const unitCost = Number.isFinite(Number(unitCostRaw)) ? Number(unitCostRaw) : 0;

  const movement = {
    movementId: baseEntry.movementId || generateMovementId(),
    schemaVersion: Number(baseEntry.schemaVersion) || MOVEMENT_SCHEMA_VERSION,
    ledgerSequence:
      baseEntry.ledgerSequence != null && Number.isFinite(Number(baseEntry.ledgerSequence))
        ? Number(baseEntry.ledgerSequence)
        : null,
    storeId,
    locationId: baseEntry.locationId != null ? String(baseEntry.locationId) : String(location),
    productId: Number(baseEntry.productId),
    lotId: baseEntry.lotId != null ? String(baseEntry.lotId) : null,
    movementType: String(baseEntry.movementType || STOCK_MOVEMENT_TYPES.ADJUSTMENT),
    quantity: Number.isFinite(qty) ? qty : 0,
    quantitySigned: Number.isFinite(qty) ? qty : 0,
    quantityUom: QUANTITY_UOM_BASE,
    qtyBefore:
      baseEntry.qtyBefore != null && Number.isFinite(Number(baseEntry.qtyBefore))
        ? Number(baseEntry.qtyBefore)
        : null,
    qtyAfter:
      baseEntry.qtyAfter != null && Number.isFinite(Number(baseEntry.qtyAfter))
        ? Number(baseEntry.qtyAfter)
        : null,
    referenceType:
      baseEntry.referenceType != null
        ? String(baseEntry.referenceType)
        : inferReferenceType(baseEntry.movementType),
    referenceId: baseEntry.referenceId != null ? String(baseEntry.referenceId) : null,
    correlationId,
    location,
    toLocation: baseEntry.toLocation || null,
    unitCost,
    costMethod:
      baseEntry.costMethod != null
        ? String(baseEntry.costMethod)
        : unitCost > 0 || baseEntry.forceCostMethod
          ? COST_METHOD_FIFO
          : null,
    costMethodVersion:
      baseEntry.costMethodVersion != null
        ? Number(baseEntry.costMethodVersion)
        : unitCost > 0 || baseEntry.forceCostMethod
          ? COST_METHOD_VERSION
          : null,
    batchNumber: baseEntry.batchNumber || null,
    purchaseId: baseEntry.purchaseId || null,
    actorId: baseEntry.actorId != null ? String(baseEntry.actorId) : null,
    sessionId: baseEntry.sessionId != null ? String(baseEntry.sessionId) : null,
    deviceId: baseEntry.deviceId != null ? String(baseEntry.deviceId) : null,
    occurredAt: now,
    recordedAt,
    createdAt: now,
    note: baseEntry.note || '',
    packaging:
      baseEntry.packaging && typeof baseEntry.packaging === 'object'
        ? baseEntry.packaging
        : null,
    // Phase 5 projection side-car fields (optional, non-sequence)
    sellingUnitPrice:
      baseEntry.sellingUnitPrice != null && Number.isFinite(Number(baseEntry.sellingUnitPrice))
        ? Number(baseEntry.sellingUnitPrice)
        : null,
    supplier: baseEntry.supplier != null ? String(baseEntry.supplier) : null,
    expirationDate: baseEntry.expirationDate || null,
    lastPurchaseId: baseEntry.lastPurchaseId != null ? String(baseEntry.lastPurchaseId) : null,
    lastReceivedAt: baseEntry.lastReceivedAt || null,
    isTransfer: baseEntry.isTransfer === true,
    transferType: baseEntry.transferType || null,
    transferredFrom: baseEntry.transferredFrom != null ? String(baseEntry.transferredFrom) : null,
    sourceLotId: baseEntry.sourceLotId != null ? String(baseEntry.sourceLotId) : null,
    originalLotId: baseEntry.originalLotId != null ? String(baseEntry.originalLotId) : null,
    transferredAt: baseEntry.transferredAt || null,
    expiredProductName:
      baseEntry.expiredProductName != null ? String(baseEntry.expiredProductName) : null
  };

  return movement;
}

function inferReferenceType(movementType) {
  const t = String(movementType || '');
  if (t === STOCK_MOVEMENT_TYPES.PURCHASE) return 'purchase';
  if (t === STOCK_MOVEMENT_TYPES.SALE || t === STOCK_MOVEMENT_TYPES.SALE_RETURN) return 'sale';
  // VOID can reverse purchase (achat) or sale — default sale when unspecified
  if (t === STOCK_MOVEMENT_TYPES.VOID) return 'sale';
  if (t === STOCK_MOVEMENT_TYPES.TRANSFER_IN || t === STOCK_MOVEMENT_TYPES.TRANSFER_OUT) {
    return 'transfer';
  }
  if (t === STOCK_MOVEMENT_TYPES.EXPIRY) return 'expiry';
  if (t === STOCK_MOVEMENT_TYPES.CONSUMPTION) return 'consumption';
  if (
    t === STOCK_MOVEMENT_TYPES.PRODUCTION_CONSUME ||
    t === STOCK_MOVEMENT_TYPES.PRODUCTION_OUTPUT
  ) {
    return 'production';
  }
  return 'adjustment';
}

/**
 * Move oldest active movements into archive partition (never delete).
 * @param {object} parsed
 * @param {number} softMax — if > 0 and list exceeds, archive overflow
 */
export function maybeArchiveActivePartition(parsed, softMax = ACTIVE_PARTITION_SOFT_MAX) {
  if (!softMax || softMax <= 0) return;
  const list = ensureStockMovementsArray(parsed);
  if (list.length <= softMax) return;
  const archive = ensureStockMovementsArchiveArray(parsed);
  const overflow = list.length - softMax;
  const moved = list.splice(0, overflow);
  for (const m of moved) {
    archive.push({ ...m, archivedAt: new Date().toISOString() });
  }
}

/** Fields that must ride with any stockMovements write (ledger + P2 snapshots). */
export const INVENTORY_LEDGER_AUTHORITATIVE_FIELDS = [
  'stockMovements',
  'inventoryLedgerMeta',
  'stockMovementsArchive',
  'stockSnapshots'
];

/**
 * @param {object} parsed
 * @param {object[]} entries
 * @param {{
 *   defaultStoreId?: string,
 *   defaultCorrelationId?: string,
 *   businessId?: string|null,
 *   actorMeta?: object,
 *   skipSnapshots?: boolean,
 *   projectLots?: boolean,
 *   projectionStrict?: boolean
 * }} [options]
 *
 * projectLots (ADR-0016 Phase 4/5): default **true** — apply movements to lot remaining
 * (Movement → Lot projection). Opt out only for pure append/legacy recovery tests with
 * `projectLots: false`. Prefer `applyInventoryMovements` for new call sites.
 */
export function appendStockMovements(parsed, entries, options = {}) {
  if (!parsed || !Array.isArray(entries) || entries.length === 0) {
    return [];
  }
  const list = ensureStockMovementsArray(parsed);
  ensureInventoryLedgerMeta(parsed);
  ensureBusinessStores(parsed);
  const existingIds = new Set(list.map((m) => m.movementId));
  for (const m of ensureStockMovementsArchiveArray(parsed)) {
    if (m?.movementId) existingIds.add(m.movementId);
  }

  const actorMeta =
    options.actorMeta ||
    resolveMovementActorMeta(options.businessId || null, options);

  const defaultStoreId =
    options.defaultStoreId || actorMeta.storeId || resolveStoreId(parsed, {});
  const defaultCorrelationId = options.defaultCorrelationId || null;
  // Default ON — opt-out via projectLots: false (legacy dual-write / tests only)
  const projectLots = options.projectLots !== false;

  const staged = [];
  for (const raw of entries) {
    if (!raw) continue;
    const movement = normalizeStockMovement(
      {
        ...raw,
        actorId: raw.actorId != null ? raw.actorId : actorMeta.actorId,
        sessionId: raw.sessionId != null ? raw.sessionId : actorMeta.sessionId,
        deviceId: raw.deviceId != null ? raw.deviceId : actorMeta.deviceId,
        storeId: raw.storeId != null ? raw.storeId : defaultStoreId
      },
      {
        storeId: defaultStoreId,
        correlationId: defaultCorrelationId
      }
    );
    if (existingIds.has(movement.movementId)) continue;
    staged.push(movement);
  }

  if (staged.length === 0) return [];

  if (projectLots) {
    // Truth order: apply movement deltas to lots, collect qtyBefore/After while doing so
    applyMovementsToLotProjection(parsed, staged, {
      strict: options.projectionStrict !== false
    });
  } else {
    // Legacy dual-write: lots already mutated → recover before/after from final state
    assignQtyBeforeAfterForBatch(parsed, staged);
  }

  const added = [];
  for (const movement of staged) {
    if (movement.ledgerSequence == null) {
      movement.ledgerSequence = allocateLedgerSequence(parsed, movement.storeId);
    }
    list.push(movement);
    existingIds.add(movement.movementId);
    added.push(movement);
  }

  // ADR-0016: never truncate/delete. Soft-max only relocates to archive.
  maybeArchiveActivePartition(parsed, ACTIVE_PARTITION_SOFT_MAX);

  // Phase 2: stock snapshots for touched product@location
  if (!options.skipSnapshots) {
    applyStockSnapshotsAfterMovements(parsed, added);
  }

  // ADR-0017 W4: enqueue after local append (same mutation intents for create + void)
  if (
    added.length > 0 &&
    options.businessId &&
    options.enqueueCloud !== false
  ) {
    const bizId = options.businessId;
    const toSync = added.slice();
    Promise.resolve()
      .then(async () => {
        const { enqueueInventoryMovementsForCloudSync } = await import(
          '../../../services/inventoryMovementSync'
        );
        return enqueueInventoryMovementsForCloudSync(bizId, toSync, {
          reason: 'movement_append'
        });
      })
      .catch((e) => {
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            '[appendStockMovements] inventory outbox enqueue failed:',
            e?.message || e
          );
        }
      });
  }

  return added;
}

/**
 * ADR-0016 Phase 4 preferred write API: movements first, lot projection follows.
 * Equivalent to appendStockMovements(..., { projectLots: true }).
 *
 * @param {object} parsed
 * @param {object[]} entries
 * @param {object} [options]
 */
export function applyInventoryMovements(parsed, entries, options = {}) {
  return appendStockMovements(parsed, entries, {
    ...options,
    projectLots: true
  });
}

/**
 * @param {string} businessId
 * @param {object[]} entries
 * @param {{ defaultCorrelationId?: string, actorMeta?: object }} [options]
 */
export async function recordStockMovements(businessId, entries, options = {}) {
  if (!businessId || !entries?.length) return [];
  let added = [];
  const projectLots = options.projectLots !== false;
  const { queueBusinessPayloadWrite } = await import(
    '../../../infrastructure/storage/businessPayloadWrite'
  );
  await queueBusinessPayloadWrite(
    businessId,
    (parsed) => {
      added = appendStockMovements(parsed, entries, {
        ...options,
        businessId
      });
      return parsed;
    },
    {
      // Phase 5: projected lots must be authoritative with movements
      authoritativeFields: projectLots
        ? ['lots', ...INVENTORY_LEDGER_AUTHORITATIVE_FIELDS]
        : [...INVENTORY_LEDGER_AUTHORITATIVE_FIELDS]
    }
  );
  return added;
}

export function buildPurchaseMovement(lot, purchaseId = null, meta = {}) {
  // Prefer explicit receive delta — restock must not log full remaining as new receipt.
  const qty = Number(
    meta.quantity != null
      ? meta.quantity
      : lot?.remainingQuantity ?? lot?.totalQuantity ?? 0
  );
  const ref = purchaseId || lot.purchaseId || lot.id;
  return normalizeStockMovement({
    movementType: STOCK_MOVEMENT_TYPES.PURCHASE,
    productId: lot.productId,
    lotId: lot.id,
    quantity: qty,
    quantityUom: 'base',
    packaging: meta.packaging || lot?.packaging || null,
    packagingInput: meta.packagingInput || null,
    referenceId: ref,
    referenceType: 'purchase',
    correlationId: meta.correlationId || generateCorrelationId(ref),
    location: lot.location || 'Principal',
    storeId: meta.storeId || lot.storeId,
    unitCost: meta.unitCost != null ? meta.unitCost : lot.purchaseUnitPrice,
    sellingUnitPrice:
      meta.sellingUnitPrice != null
        ? meta.sellingUnitPrice
        : lot?.sellingUnitPrice,
    supplier: meta.supplier != null ? meta.supplier : lot?.supplier,
    expirationDate:
      meta.expirationDate != null ? meta.expirationDate : lot?.expirationDate,
    lastPurchaseId: meta.lastPurchaseId || (purchaseId ? String(purchaseId) : null),
    lastReceivedAt: meta.lastReceivedAt || null,
    costMethod: COST_METHOD_FIFO,
    costMethodVersion: COST_METHOD_VERSION,
    forceCostMethod: true,
    batchNumber: lot.batchNumber || meta.batchNumber || null,
    purchaseId: purchaseId || lot.purchaseId || null,
    actorId: meta.actorId,
    sessionId: meta.sessionId,
    deviceId: meta.deviceId,
    note: meta.note || 'purchase_receipt'
  });
}

/**
 * Küchenverbrauch (Restaurant) — manueller Abzug ohne Verkauf.
 */
export function buildConsumptionMovementsFromPlan(consumptionRecord, plan, meta = {}) {
  const referenceId = consumptionRecord?.id || null;
  if (!referenceId || !plan?.deductions?.length) return [];
  const correlationId =
    meta.correlationId ||
    consumptionRecord.correlationId ||
    generateCorrelationId(referenceId);

  return plan.deductions.map((d) =>
    normalizeStockMovement({
      movementType: STOCK_MOVEMENT_TYPES.CONSUMPTION,
      productId: d.productId,
      lotId: d.lotId,
      quantity: -Math.abs(Number(d.quantity)),
      referenceId,
      referenceType: 'consumption',
      correlationId,
      location: d.location || 'Principal',
      storeId: meta.storeId || d.storeId || consumptionRecord.storeId,
      unitCost: Number(d.unitCost ?? d.unitPrice) || 0,
      costMethod: COST_METHOD_FIFO,
      costMethodVersion: COST_METHOD_VERSION,
      actorId: meta.actorId || consumptionRecord.actorId,
      sessionId: meta.sessionId || consumptionRecord.sessionId,
      packaging: resolvePackagingAuditMeta(d, meta),
      note: consumptionRecord.note || 'kitchen_consumption'
    })
  );
}

/**
 * @param {object} saleData
 * @param {object} plan — planSaleLotDeductions result
 * @param {object} [meta]
 */
export function buildSaleMovementsFromPlan(saleData, plan, meta = {}) {
  const referenceId = saleData?.orderId || saleData?.id || null;
  if (!referenceId || !plan?.deductions?.length) return [];

  const correlationId =
    meta.correlationId ||
    saleData.inventoryCorrelationId ||
    saleData.correlationId ||
    generateCorrelationId(referenceId);

  // lotId → unitCost from usedLots when deductions omit unit cost
  const unitCostByLot = new Map();
  for (const r of plan.results || []) {
    for (const u of r.usedLots || []) {
      if (u?.lotId == null) continue;
      const c = Number(u.unitPrice ?? u.unitCost ?? u.purchaseUnitPrice);
      if (Number.isFinite(c)) unitCostByLot.set(String(u.lotId), c);
    }
  }

  return plan.deductions.map((d) => {
    const unitCost =
      Number(d.unitCost ?? d.unitPrice ?? unitCostByLot.get(String(d.lotId))) || 0;
    const packMeta = resolvePackagingAuditMeta(
      d,
      meta,
      {
        productsPerPackage: d.productsPerPackage,
        unitsPerPack: d.unitsPerPack,
        packagingType: d.packagingType
      }
    );
    return normalizeStockMovement({
      movementType: STOCK_MOVEMENT_TYPES.SALE,
      productId: d.productId,
      lotId: d.lotId,
      quantity: -Math.abs(Number(d.quantity)),
      referenceId,
      referenceType: 'sale',
      correlationId,
      location: d.location || saleData.location || 'Principal',
      storeId: meta.storeId || saleData.storeId || d.storeId,
      unitCost,
      costMethod: COST_METHOD_FIFO,
      costMethodVersion: COST_METHOD_VERSION,
      forceCostMethod: true,
      actorId: meta.actorId || saleData.actorId || saleData.userId,
      sessionId: meta.sessionId || saleData.sessionId || saleData.cashRegisterSessionId,
      deviceId: meta.deviceId || saleData.deviceId,
      packaging: packMeta,
      note: meta.note || 'sale_checkout'
    });
  });
}

export function buildExpiryMovement(expiredProduct, lot = null, meta = {}) {
  const qty = Number(expiredProduct?.quantity ?? lot?.remainingQuantity ?? 0);
  const ref = expiredProduct.lotId
    ? `expiry_${expiredProduct.lotId}`
    : `expiry_${expiredProduct.id}`;
  const unitCost =
    Number(expiredProduct.purchaseUnitPrice || lot?.purchaseUnitPrice) || 0;
  return normalizeStockMovement({
    movementType: STOCK_MOVEMENT_TYPES.EXPIRY,
    productId: expiredProduct.productId,
    lotId: expiredProduct.lotId || lot?.id || null,
    quantity: -Math.abs(qty),
    referenceId: ref,
    referenceType: 'expiry',
    correlationId: meta.correlationId || generateCorrelationId(ref),
    location: expiredProduct.stockLocation || lot?.location || 'Principal',
    storeId: meta.storeId || expiredProduct.storeId || lot?.storeId,
    unitCost,
    costMethod: COST_METHOD_FIFO,
    costMethodVersion: COST_METHOD_VERSION,
    forceCostMethod: unitCost > 0,
    batchNumber: expiredProduct.batchNumber || lot?.batchNumber,
    actorId: meta.actorId,
    sessionId: meta.sessionId,
    packaging: resolvePackagingAuditMeta(meta, lot, expiredProduct),
    note: meta.note || 'expired_product',
    expiredProductName:
      expiredProduct.expiredProductName || meta.expiredProductName || null
  });
}

/**
 * Manual lot remainingQuantity change (signed delta: + increase / − decrease).
 */
export function buildAdjustmentMovement(lot, quantityDelta, note = 'lot_quantity_adjust', meta = {}) {
  const delta = Number(quantityDelta) || 0;
  const ref = lot?.id ? `adj_${lot.id}` : null;
  return normalizeStockMovement({
    movementType: STOCK_MOVEMENT_TYPES.ADJUSTMENT,
    productId: lot?.productId,
    lotId: lot?.id,
    quantity: delta,
    referenceId: ref,
    referenceType: meta.referenceType || 'adjustment',
    correlationId: meta.correlationId || generateCorrelationId(ref || note),
    location: lot?.location || 'Principal',
    storeId: meta.storeId || lot?.storeId,
    unitCost: lot?.purchaseUnitPrice || 0,
    costMethod: COST_METHOD_FIFO,
    costMethodVersion: COST_METHOD_VERSION,
    batchNumber: lot?.batchNumber || null,
    actorId: meta.actorId,
    sessionId: meta.sessionId,
    packaging: resolvePackagingAuditMeta(meta, lot),
    note,
    purchaseId: meta.purchaseId || lot?.purchaseId || null
  });
}

/**
 * Soft-void / reverse (signed). Negative = reverse PURCHASE intake; positive = restore.
 * ADR-0016: preferential reverse typing vs bare ADJUSTMENT for achat void.
 */
export function buildVoidMovement(lot, quantityDelta, note = 'void', meta = {}) {
  const delta = Number(quantityDelta) || 0;
  const ref =
    meta.referenceId ||
    lot?.purchaseId ||
    (lot?.id ? `void_${lot.id}` : null);
  return normalizeStockMovement({
    movementType: STOCK_MOVEMENT_TYPES.VOID,
    productId: lot?.productId,
    lotId: lot?.id,
    quantity: delta,
    referenceId: ref,
    referenceType: meta.referenceType || (delta < 0 ? 'purchase' : 'sale'),
    correlationId: meta.correlationId || generateCorrelationId(ref || note),
    location: lot?.location || 'Principal',
    storeId: meta.storeId || lot?.storeId,
    unitCost: lot?.purchaseUnitPrice || 0,
    costMethod: COST_METHOD_FIFO,
    costMethodVersion: COST_METHOD_VERSION,
    batchNumber: lot?.batchNumber || null,
    actorId: meta.actorId,
    sessionId: meta.sessionId,
    packaging: resolvePackagingAuditMeta(meta, lot),
    note,
    purchaseId: meta.purchaseId || lot?.purchaseId || null
  });
}

/**
 * @param {number} delta
 * @param {'adjustment'|'purchase_void'|'purchase_delta'} policy
 * @returns {string} STOCK_MOVEMENT_TYPES value
 */
function resolveDeltaMovementType(delta, policy = 'adjustment') {
  const p = String(policy || 'adjustment');
  if (p === 'purchase_void') {
    // Achat void/reverse: down = VOID (purchase reverse), up = PURCHASE restock
    return delta < 0 ? STOCK_MOVEMENT_TYPES.VOID : STOCK_MOVEMENT_TYPES.PURCHASE;
  }
  if (p === 'purchase_delta') {
    // Rebuild / plan from purchase lines: +PURCHASE, −VOID
    return delta < 0 ? STOCK_MOVEMENT_TYPES.VOID : STOCK_MOVEMENT_TYPES.PURCHASE;
  }
  return STOCK_MOVEMENT_TYPES.ADJUSTMENT;
}

/**
 * Diff remainingQuantity before→after (by lot id).
 * Creates → +rem; deletes → −oldRem; updates → after−before.
 *
 * @param {object} [meta]
 * @param {'adjustment'|'purchase_void'|'purchase_delta'} [meta.movementTypePolicy]
 */
export function buildLotRemainingDeltaMovements(
  beforeLots = [],
  afterLots = [],
  note = 'lot_remaining_sync',
  meta = {}
) {
  const toList = (raw) =>
    Array.isArray(raw) ? raw : raw && typeof raw === 'object' ? Object.values(raw) : [];
  const beforeMap = new Map();
  for (const lot of toList(beforeLots)) {
    if (lot?.id == null) continue;
    beforeMap.set(String(lot.id), lot);
  }
  const afterMap = new Map();
  for (const lot of toList(afterLots)) {
    if (lot?.id == null) continue;
    afterMap.set(String(lot.id), lot);
  }
  const ids = new Set([...beforeMap.keys(), ...afterMap.keys()]);
  const movements = [];
  const correlationId = meta.correlationId || generateCorrelationId(note);
  const policy = meta.movementTypePolicy || 'adjustment';
  for (const id of ids) {
    const before = beforeMap.get(id);
    const after = afterMap.get(id);
    const beforeRem = Number(before?.remainingQuantity) || 0;
    const afterRem = after ? Number(after.remainingQuantity) || 0 : 0;
    const delta = afterRem - beforeRem;
    if (delta === 0) continue;
    const lot = after || before;
    const movementType = resolveDeltaMovementType(delta, policy);
    const shared = { ...meta, correlationId };
    if (movementType === STOCK_MOVEMENT_TYPES.VOID) {
      movements.push(
        buildVoidMovement(lot, delta, note, {
          ...shared,
          referenceType: shared.referenceType || 'purchase'
        })
      );
    } else if (movementType === STOCK_MOVEMENT_TYPES.PURCHASE) {
      movements.push(
        buildPurchaseMovement(lot, lot?.purchaseId || shared.purchaseId || null, {
          ...shared,
          quantity: Math.abs(delta),
          note
        })
      );
    } else {
      movements.push(buildAdjustmentMovement(lot, delta, note, shared));
    }
  }
  return movements;
}

/**
 * @param {object} params
 */
export function buildTransferMovements({
  productId,
  lotId,
  fromLocation,
  toLocation,
  quantity,
  referenceId,
  unitCost = 0,
  sellingUnitPrice = null,
  batchNumber = null,
  destinationLotId = null,
  storeId = null,
  fromStoreId = null,
  toStoreId = null,
  correlationId = null,
  actorId = null,
  sessionId = null,
  isTransfer = true,
  transferredFrom = null,
  originalLotId = null,
  packaging = null,
  sourceLot = null
}) {
  const qty = Math.abs(Number(quantity));
  const ref = referenceId || `transfer_${Date.now()}`;
  const corr = correlationId || generateCorrelationId(ref);
  const sourceLotId = lotId;
  const outStore = fromStoreId || storeId || null;
  const inStore = toStoreId || storeId || null;
  const interStore =
    outStore != null &&
    inStore != null &&
    String(outStore) !== String(inStore);
  const transferType = interStore ? 'store_transfer' : 'location_transfer';
  const packMeta =
    packaging ||
    packagingAuditMetaFromCatalog(sourceLot || {}) ||
    null;
  return [
    normalizeStockMovement({
      movementType: STOCK_MOVEMENT_TYPES.TRANSFER_OUT,
      productId,
      lotId,
      quantity: -qty,
      referenceId: ref,
      referenceType: 'transfer',
      correlationId: corr,
      location: fromLocation || 'Principal',
      toLocation: toLocation || 'Principal',
      storeId: outStore,
      unitCost,
      costMethod: COST_METHOD_FIFO,
      costMethodVersion: COST_METHOD_VERSION,
      forceCostMethod: unitCost > 0,
      batchNumber,
      actorId,
      sessionId,
      packaging: packMeta,
      isTransfer: true,
      transferType,
      note: interStore ? 'store_transfer_source' : 'transfer_source'
    }),
    normalizeStockMovement({
      movementType: STOCK_MOVEMENT_TYPES.TRANSFER_IN,
      productId,
      lotId: destinationLotId || null,
      quantity: qty,
      referenceId: ref,
      referenceType: 'transfer',
      correlationId: corr,
      location: toLocation || 'Principal',
      toLocation: fromLocation || 'Principal',
      storeId: inStore,
      unitCost,
      costMethod: COST_METHOD_FIFO,
      costMethodVersion: COST_METHOD_VERSION,
      forceCostMethod: unitCost > 0,
      batchNumber,
      sellingUnitPrice,
      actorId,
      sessionId,
      packaging: packMeta,
      isTransfer: true,
      transferType,
      transferredFrom: transferredFrom || fromLocation,
      originalLotId: originalLotId || sourceLotId,
      note: interStore ? 'store_transfer_destination' : 'transfer_destination'
    })
  ];
}

/**
 * Build frozen COGS snapshot for a completed sale (ADR-0016).
 * @param {object} saleData
 * @param {object} plan
 */
export function buildSaleCostSnapshot(saleData, plan) {
  const deductions = plan?.deductions || [];
  const unitCostByLot = new Map();
  for (const r of plan?.results || []) {
    for (const u of r.usedLots || []) {
      if (u?.lotId == null) continue;
      const c = Number(u.unitPrice ?? u.unitCost ?? u.purchaseUnitPrice);
      if (Number.isFinite(c)) unitCostByLot.set(String(u.lotId), c);
    }
  }

  const lotIds = [];
  const unitCosts = [];
  const quantities = [];
  let extendedCost = 0;

  for (const d of deductions) {
    const q = Math.abs(Number(d.quantity) || 0);
    const uc =
      Number(d.unitCost ?? d.unitPrice ?? unitCostByLot.get(String(d.lotId))) || 0;
    lotIds.push(d.lotId != null ? String(d.lotId) : null);
    unitCosts.push(uc);
    quantities.push(q);
    extendedCost += q * uc;
  }

  const totalFromPlan = Number(plan?.totalCost);
  const cost =
    Number.isFinite(totalFromPlan) && totalFromPlan >= 0
      ? totalFromPlan
      : extendedCost;

  return {
    costMethod: COST_METHOD_FIFO,
    costMethodVersion: COST_METHOD_VERSION,
    inventoryCost: cost,
    extendedCost: cost,
    lotIds,
    unitCost: unitCosts,
    quantities,
    frozen: true,
    capturedAt: new Date().toISOString(),
    inventoryCorrelationId:
      saleData?.inventoryCorrelationId ||
      saleData?.correlationId ||
      generateCorrelationId(saleData?.orderId || saleData?.id)
  };
}

/**
 * @param {object} parsed
 * @param {object} [filters]
 */
export function listStockMovements(parsed, filters = {}) {
  const active = ensureStockMovementsArray(parsed);
  const archive = filters.includeArchive ? ensureStockMovementsArchiveArray(parsed) : [];
  const list = filters.includeArchive ? active.concat(archive) : active;
  return list.filter((m) => {
    if (filters.lotId != null && String(m.lotId || '') !== String(filters.lotId)) {
      return false;
    }
    if (filters.productId != null && String(m.productId) !== String(filters.productId)) {
      return false;
    }
    if (filters.location && m.location !== filters.location) return false;
    if (filters.storeId && String(m.storeId || DEFAULT_STORE_ID) !== String(filters.storeId)) {
      return false;
    }
    if (filters.movementType && m.movementType !== filters.movementType) return false;
    if (
      filters.correlationId &&
      String(m.correlationId || '') !== String(filters.correlationId)
    ) {
      return false;
    }
    if (
      filters.ledgerSequenceFrom != null &&
      (Number(m.ledgerSequence) || 0) < Number(filters.ledgerSequenceFrom)
    ) {
      return false;
    }
    if (filters.since && new Date(m.createdAt || m.occurredAt) < new Date(filters.since)) {
      return false;
    }
    if (filters.until && new Date(m.createdAt || m.occurredAt) > new Date(filters.until)) {
      return false;
    }
    return true;
  });
}

/**
 * Summe signed quantities pro Produkt (Ledger-Wahrheit).
 */
export function sumMovementQuantityForProduct(parsed, productId, location = null) {
  return listStockMovements(parsed, { productId, location, includeArchive: true }).reduce(
    (sum, m) => sum + Number(m.quantity || 0),
    0
  );
}
